import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { buildApiUrl, getApiHeaders } from '../../support/queriesHelpers';
import { RootState } from '../../support/store';
import {
  Search,
  ChevronUp,
  ChevronDown,
  MessageSquare,
  Bookmark,
  Share2,
  X,
  Play,
  ExternalLink,
  Send,
  Heart,
  Trash2,
  Shield,
  Plus,
  Eye,
  Edit3,
  Hash,
  TrendingUp,
  Upload,
  Check,
  AtSign,
  Loader2,
  AlignLeft,
  ImageIcon,
  Film,
  Layers,
} from 'lucide-react';
import MediaGrid from './MediaGrid';
import MediaComposer from './MediaComposer';
import {
  API_BASE,
  ComposerItem,
  PostMediaItem,
  buildMediaPatch,
  buildMediaPayload,
  canEditMedia,
  toComposerItems,
  extractYouTubeId,
  mapMediaList,
  readVideoDuration,
  releaseItems,
} from './mediaUtils';


interface Comment {
  id: string
  author: string
  avatar: string
  text: string
  timestamp: number
  likes: number
  likedByMe: boolean
  isOwner: boolean
}

interface Post {
  id: string
  author: string
  avatar: string
  authorEmail: string
  title: string
  description: string
  category: string
  tags: string[]
  postType: 'video' | 'text' | 'image'
  videoUrl: string
  videoSource: 'vidcast' | 'youtube' | 'upload'
  thumbnailUrl: string
  duration: string
  imageUrl: string
  videoFileUrl: string
  hasImage: boolean
  hasVideoFile: boolean
  upvotes: number
  downvotes: number
  myVote: 'up' | 'down' | null
  liked: boolean
  commentsCount: number
  bookmarked: boolean
  timestamp: number
  views: number
  isOwner: boolean
  media: PostMediaItem[]
}


const CATEGORIES = [
  { id: 'all', label: 'All Posts', color: '#3b82f6' },
  { id: 'security', label: 'Security', color: '#22c55e' },
  { id: 'networking', label: 'Networking', color: '#14b8a6' },
  { id: 'cloud', label: 'Cloud', color: '#3b82f6' },
  { id: 'demos', label: 'Demos', color: '#f97316' },
  { id: 'tips', label: 'Tips & Tricks', color: '#a855f7' },
];

const CATEGORY_MAP: Record<string, string> = {
  'security': 'Security',
  'networking': 'Networking',
  'cloud': 'Cloud',
  'demos': 'Demos',
  'tips': 'Tips & Tricks',
};

const SORT_OPTIONS = ['Hot', 'New', 'Top', 'Most Discussed'];

const SORT_PARAM_MAP: Record<string, string> = {
  Hot: 'hot',
  New: 'new',
  Top: 'top',
  'Most Discussed': 'discussed',
};


async function apiFetch(url: string, opts: RequestInit = {}) {
  const method = (opts.method as string) || 'GET';
  const res = await fetch(buildApiUrl(url), { ...opts, headers: { ...getApiHeaders(null, method, url), ...(opts.headers as Record<string, string> || {}) } });
  if (res.status === 204) return null;
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

interface PresignedUpload {
  upload_url: string
  object_key: string
  content_type: string
  expires_in: number
}

async function uploadImageViaPresign(file: File): Promise<string> {
  const presign = await apiFetch(`${API_BASE}/posts/presign-image/`, {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, content_type: file.type || 'application/octet-stream' }),
  }) as PresignedUpload;

  const putRes = await fetch(presign.upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': presign.content_type || file.type || 'application/octet-stream' },
    body: file,
  });
  if (!putRes.ok) throw new Error(`upload_${putRes.status}`);
  return presign.object_key;
}

async function uploadVideoViaPresign(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  const presign = await apiFetch(`${API_BASE}/posts/presign-video/`, {
    method: 'POST',
    body: JSON.stringify({ content_type: file.type || 'video/mp4' }),
  }) as PresignedUpload;

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presign.upload_url, true);
    xhr.setRequestHeader('Content-Type', presign.content_type || file.type || 'video/mp4');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`upload_${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('upload_network_error'));
    xhr.ontimeout = () => reject(new Error('upload_timeout'));
    xhr.timeout = 0;
    xhr.send(file);
  });

  return presign.object_key;
}

async function enrichPostWithMediaUrls(post: any, postId: string): Promise<any> {
  const enriched = { ...post };
  try {
    if (post.post_type === 'image' && post.image) {
      const { url } = await apiFetch(`${API_BASE}/posts/${postId}/image-url/`) as { url: string; expires_in?: number };
      enriched._imageUrl = url;
    } else if (post.post_type === 'video' && post.video_file) {
      const { url } = await apiFetch(`${API_BASE}/posts/${postId}/video-url/`) as { url: string; expires_in?: number };
      enriched._videoFileUrl = url;
    }
  } catch {
    // If temp URL fetch fails, PrivateMedia will fetch it using useMediaUrl
  }
  return enriched;
}

function mapPost(p: any, currentEmail: string | null): Post {
  const initials =
    p.author?.avatar_initials ||
    (p.author?.display_name || '').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() ||
    'U';
  return {
    id: p.id ?? p.pk ?? p.uuid,
    author: p.author?.display_name || p.author?.email || 'Unknown',
    avatar: initials,
    authorEmail: p.author?.email || '',
    title: p.title,
    description: p.description || '',
    category: p.category,
    tags: p.tags || [],
    postType: (p.post_type as 'video' | 'text' | 'image') || 'video',
    videoUrl: p.video_url || '',
    videoSource: p.video_source as 'vidcast' | 'youtube',
    thumbnailUrl:
      p.video_source === 'youtube'
        ? `https://img.youtube.com/vi/${extractYouTubeId(p.video_url)}/maxresdefault.jpg`
        : '',
    duration: p.duration || '0:00',
    imageUrl: p._imageUrl || ((typeof p.image === 'string' && p.image.startsWith('http')) ? p.image : ''),
    videoFileUrl: p._videoFileUrl || ((typeof p.video_file === 'string' && p.video_file.startsWith('http')) ? p.video_file : ''),
    hasImage: (p.post_type === 'image') && !!p.image,
    hasVideoFile: !!(p.video_file),
    upvotes: p.upvotes ?? 0,
    downvotes: p.downvotes ?? 0,
    myVote: p.user_vote as 'up' | 'down' | null ?? null,
    liked: p.user_liked ?? false,
    commentsCount: p.comments_count ?? 0,
    bookmarked: p.user_bookmarked ?? false,
    timestamp: new Date(p.created_at).getTime(),
    views: p.views ?? 0,
    isOwner: !!currentEmail && p.author?.email === currentEmail,
    media: mapMediaList(p.media),
  };
}

function mapComment(c: any, currentEmail: string | null): Comment {
  const initials =
    c.author?.avatar_initials ||
    (c.author?.display_name || '').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase() ||
    'U';
  return {
    id: c.id,
    author: c.author?.display_name || c.author?.email || 'Unknown',
    avatar: initials,
    text: c.text,
    timestamp: new Date(c.created_at).getTime(),
    likes: c.likes_count || 0,
    likedByMe: c.user_liked || false,
    isOwner: !!currentEmail && c.author?.email === currentEmail,
  };
}


interface MediaUrlState {
  url: string | null
  loading: boolean
  error: boolean
}

function useMediaUrl(
  postId: string,
  type: 'image' | 'video',
  enabled: boolean,
): MediaUrlState {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const fetchRef = useRef<((retry?: number) => void) | null>(null);

  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  fetchRef.current = async (retryCount = 0) => {
    if (!enabled || !postId) return;
    setLoading(true);
    setError(false);
    clearTimer();
    try {
      const path =
        type === 'image'
          ? `${API_BASE}/posts/${postId}/image-url/`
          : `${API_BASE}/posts/${postId}/video-url/`;
      const endpoint = buildApiUrl(path);
      const headers = getApiHeaders(null, 'GET', path);
      const res = await fetch(endpoint, { headers });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      if (!mountedRef.current) return;
      setUrl(data.url);
      setError(false);
      const refreshIn = (data.refresh_after ?? 3300) * 1000;
      timerRef.current = setTimeout(() => fetchRef.current?.(), refreshIn);
    } catch {
      if (!mountedRef.current) return;
      setError(true);
      if (retryCount < 1) {
        timerRef.current = setTimeout(() => fetchRef.current?.(retryCount + 1), 3000);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) fetchRef.current?.();
    return () => {
      mountedRef.current = false;
      clearTimer();
    };
  }, [postId, type, enabled]);  

  return { url, loading, error };
}

function PrivateMedia({
  postId,
  type,
  alt = '',
  className = '',
  preloadedUrl = '',
}: {
  postId: string
  type: 'image' | 'video'
  alt?: string
  className?: string
  preloadedUrl?: string
}) {
  const { url: fetchedUrl, loading, error } = useMediaUrl(postId, type, !preloadedUrl);
  const url = preloadedUrl || fetchedUrl;

  if (loading && !url) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
      </div>
    );
  }

  if (error && !url) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 text-xs text-gray-400 ${className}`}>
        Media unavailable
      </div>
    );
  }

  if (!url) return null;

  if (type === 'image') {
    return <img src={url} alt={alt} className={className} />;
  }

  return (
    <video
      src={url}
      controls
      className={className}
      style={{ maxHeight: '20rem' }}
    />
  );
}


function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  const years = Math.floor(days / 365);
  const months = Math.floor((days % 365) / 30);
  return months ? `${years}y ${months}mo ago` : `${years}y ago`;
}

function getAvatarColor(initials: string): string {
  const colors: Record<string, string> = {
    RM: 'bg-[#f43f5e]',
    AK: 'bg-[#10b981]',
    CW: 'bg-[#475569]',
    TL: 'bg-[#0ea5e9]',
    LP: 'bg-[#d946ef]',
    DM: 'bg-[#8b5cf6]',
    ME: 'bg-[#14b8a6]',
  };
  return colors[initials] || 'bg-[#14b8a6]';
}

function getCategoryColor(cat: string): string {
  const found = CATEGORIES.find(c => c.id === cat);
  return found ? found.color : '#22c55e';
}

function getCategoryLabel(cat: string): string {
  return CATEGORY_MAP[cat] || cat;
}

function renderCommentText(text: string): React.ReactNode {
  const parts = text.split(/(@\w+(?:\s\w+)?)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      return (
        <span key={i} className="inline-flex items-center gap-0.5 px-1 py-0 bg-[#eff6ff] text-[#2563eb] rounded font-medium text-[13px]">
          <AtSign className="w-3 h-3" />
          {part.slice(1)}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

const KNOWN_USERS = ['Rachel Morgan', 'Alex Kim', 'Chris Wang', 'Tom Lee', 'Lisa Park', 'David Martinez'];


function VideoPlayer({ post, onClose }: { post: Post; onClose: () => void }) {
  const ytId = extractYouTubeId(post.videoUrl);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-4xl mx-4">
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/80 hover:text-white transition">
          <X className="w-6 h-6" />
        </button>
        {post.videoSource === 'youtube' && ytId ? (
          <div className="aspect-video rounded-xl overflow-hidden shadow-2xl">
            <iframe
              referrerPolicy="strict-origin-when-cross-origin"
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={post.title}
            />
          </div>
        ) : (
          <div className="aspect-video rounded-xl overflow-hidden shadow-2xl">
            <iframe
              referrerPolicy="strict-origin-when-cross-origin"
              src={post.videoUrl}
              className="w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={post.title}
            />
          </div>
        )}
      </div>
    </div>
  );
}


function CommentSection({ postId, comments, onAddComment, onLikeComment, onDeleteComment, loadingComments }: {
  postId: string
  comments: Comment[]
  loadingComments: boolean
  onAddComment: (postId: string, text: string) => void
  onLikeComment: (postId: string, commentId: string) => void
  onDeleteComment: (postId: string, commentId: string) => void
}) {
  const [text, setText] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleTextChange = (value: string) => {
    setText(value);
    const lastAt = value.lastIndexOf('@');
    if (lastAt !== -1 && lastAt === value.length - 1) {
      setShowMentions(true);
      setMentionFilter('');
    } else if (lastAt !== -1 && !value.slice(lastAt).includes(' ')) {
      setShowMentions(true);
      setMentionFilter(value.slice(lastAt + 1).toLowerCase());
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (name: string) => {
    const lastAt = text.lastIndexOf('@');
    const before = text.slice(0, lastAt);
    setText(`${before}@${name} `);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const filteredUsers = KNOWN_USERS.filter(u => u.toLowerCase().includes(mentionFilter));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    onAddComment(postId, text.trim());
    setText('');
    setShowMentions(false);
  };

  if (loadingComments) {
    return (
      <div className="mt-4 pt-4 border-t border-gray-100 flex justify-center py-4">
        <Loader2 className="w-5 h-5 text-gray-300 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
      {comments.map((c) => (
        <div key={c.id} className="flex items-start gap-2.5">
          <div className={`w-6 h-6 rounded-full ${getAvatarColor(c.avatar)} flex items-center justify-center text-white text-[9px] font-bold shrink-0`}>
            {c.avatar}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-800">{c.author}</span>
              <span className="text-[11px] text-gray-400">{timeAgo(c.timestamp)}</span>
            </div>
            <p className="text-sm text-gray-600 mt-0.5">{renderCommentText(c.text)}</p>
            <div className="flex items-center gap-3 mt-1.5">
              <button
                onClick={() => onLikeComment(postId, c.id)}
                className={`flex items-center gap-1 text-[11px] transition ${c.likedByMe ? 'text-[#f43f5e]' : 'text-gray-400 hover:text-[#f43f5e]'}`}
              >
                <Heart className="w-3 h-3" fill={c.likedByMe ? 'currentColor' : 'none'} />
                {c.likes}
              </button>
              {c.isOwner && (
                <button
                  onClick={() => onDeleteComment(postId, c.id)}
                  className="text-[11px] text-gray-400 hover:text-[#ef4444] transition"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
      <form onSubmit={handleSubmit} className="flex items-center gap-2 mt-2 relative">
        <div className="w-6 h-6 rounded-full bg-[#14b8a6] flex items-center justify-center text-white text-[9px] font-bold shrink-0">ME</div>
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            placeholder="Add a comment... (use @ to mention)"
            className="w-full text-sm bg-gray-100 border border-gray-200 rounded-full px-4 py-1.5 text-gray-700 focus:outline-none focus:border-[#60a5fa] placeholder-gray-400"
          />
          {showMentions && filteredUsers.length > 0 && (
            <div className="absolute bottom-full left-0 mb-1 w-56 bg-white rounded-xl border border-gray-200 shadow-lg py-1 z-50">
              {filteredUsers.slice(0, 5).map(user => (
                <button
                  key={user}
                  type="button"
                  onClick={() => insertMention(user)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-[#eff6ff] transition"
                >
                  <div className={`w-5 h-5 rounded-full ${getAvatarColor(user.split(' ').map(n => n[0]).join(''))} flex items-center justify-center text-white text-[8px] font-bold`}>
                    {user.split(' ').map(n => n[0]).join('')}
                  </div>
                  <span className="text-xs font-medium text-gray-700">{user}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          disabled={!text.trim()}
          className="p-1.5 rounded-full bg-[#3b82f6] text-white disabled:opacity-30 hover:bg-[#2563eb] transition"
        >
          <Send className="w-3.5 h-3.5" />
        </button>
      </form>
    </div>
  );
}


type NewPostData =
  | { postType: 'text'; title: string; description: string; category: string; tags: string[] }
  | { postType: 'image'; title: string; description: string; category: string; tags: string[]; imageFile: File }
  | { postType: 'video'; title: string; description: string; category: string; tags: string[]; videoUrl: string; videoSource: 'vidcast' | 'youtube'; duration?: string }
  | { postType: 'video'; title: string; description: string; category: string; tags: string[]; videoFile: File; videoSource: 'upload' }
  | { postType: 'album'; title: string; description: string; category: string; tags: string[]; items: ComposerItem[] }

function NewPostModal({ isOpen, onClose, onSubmit, onUploadProgress }: {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: NewPostData) => void
  onUploadProgress: (pct: number | null) => void
}) {
  const [postType, setPostType] = useState<'text' | 'image' | 'video' | 'album'>('text');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('security');
  const [tagsInput, setTagsInput] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoSource, setVideoSource] = useState<'vidcast' | 'youtube' | 'upload'>('youtube');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [detectedDuration, setDetectedDuration] = useState<string | null>(null);
  const [detectingDuration, setDetectingDuration] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [localUploadPct, setLocalUploadPct] = useState<number | null>(null);
  const [albumItems, setAlbumItems] = useState<ComposerItem[]>([]);

  const reportProgress = (pct: number | null) => {
    setLocalUploadPct(pct);
    onUploadProgress(pct);
  };

  useEffect(() => {
    if (postType !== 'video' || videoSource !== 'youtube' || !videoUrl) { setDetectedDuration(null); return; }
    const ytId = extractYouTubeId(videoUrl);
    if (!ytId) { setDetectedDuration(null); return; }
    setDetectingDuration(true);
    fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${ytId}`)
      .then(r => r.json())
      .then(data => { if (data.title && !title) setTitle(data.title); })
      .catch(() => {})
      .then(() => setDetectingDuration(false));
  }, [videoUrl, videoSource, postType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageSelect = (file: File) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const reset = () => {
    setTitle(''); setDescription(''); setCategory('security'); setTagsInput('');
    setVideoUrl(''); setVideoSource('youtube'); setVideoFile(null);
    setDetectedDuration(null); setDetectingDuration(false);
    setImageFile(null); setImagePreview(null);
    releaseItems(albumItems); setAlbumItems([]);
    reportProgress(null);
    onClose();
  };

  const canSubmit = () => {
    if (localUploadPct !== null) return false;
    if (!title.trim()) return false;
    if (postType === 'video') {
      if (videoSource === 'upload') return !!videoFile;
      return !!videoUrl.trim();
    }
    if (postType === 'image') return !!imageFile;
    if (postType === 'album') return albumItems.length > 0;
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit()) return;
    const tags = tagsInput.split(',').map(t => t.trim().replace(/^#/, '')).filter(Boolean);
    if (postType === 'text') {
      onSubmit({ postType: 'text', title: title.trim(), description: description.trim(), category, tags });
    } else if (postType === 'album') {
      const [only] = albumItems;
      const base = { title: title.trim(), description: description.trim(), category, tags };
      if (albumItems.length === 0 || only.kind === 'existing') return;
      if (albumItems.length > 1) onSubmit({ ...base, postType: 'album', items: albumItems });
      else if (only.kind === 'image') onSubmit({ ...base, postType: 'image', imageFile: only.file });
      else if (only.source === 'upload') onSubmit({ ...base, postType: 'video', videoFile: only.file, videoSource: 'upload' });
      else onSubmit({ ...base, postType: 'video', videoUrl: only.url, videoSource: only.source });
    } else if (postType === 'image') {
      if (!imageFile) return;
      onSubmit({ postType: 'image', title: title.trim(), description: description.trim(), category, tags, imageFile });
    } else if (videoSource === 'upload') {
      if (!videoFile) return;
      onSubmit({ postType: 'video', title: title.trim(), description: description.trim(), category, tags, videoFile, videoSource: 'upload' });
    } else {
      onSubmit({ postType: 'video', title: title.trim(), description: description.trim(), category, tags, videoUrl: videoUrl.trim(), videoSource, duration: detectedDuration || undefined });
    }
    reset();
  };

  if (!isOpen) return null;

  const parsedTags = tagsInput
    .split(',')
    .map(t => t.trim().replace(/^#/, ''))
    .filter(Boolean)
    .slice(0, 8);

  const typeOptions: { id: 'text' | 'image' | 'video' | 'album'; label: string; Icon: React.FC<{ className?: string }> }[] = [
    { id: 'text', label: 'Text', Icon: AlignLeft },
    { id: 'image', label: 'Photo', Icon: ImageIcon },
    { id: 'video', label: 'Video', Icon: Film },
    { id: 'album', label: 'Album', Icon: Layers },
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={reset} />
      <div className="relative w-full max-w-3xl max-h-[92vh] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl">
        <div className="border-b border-gray-100 px-6 pt-5 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500">Publishing Workspace</p>
              <h2 className="mt-1 text-xl font-bold text-gray-900">Create a new post</h2>
              <p className="mt-1 text-sm text-gray-500">Choose a format, add context, and publish to the P2P feed.</p>
            </div>
            <button
              onClick={reset}
              className="shrink-0 rounded-lg border border-gray-200 p-1.5 text-gray-400 transition hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-4">
            {typeOptions.map(({ id, label, Icon }) => {
              const active = postType === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPostType(id)}
                  className={`group rounded-lg border px-3 py-2.5 text-left transition ${
                    active
                      ? 'border-[#93c5fd] bg-[#eff6ff] shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${active ? 'bg-[#3b82f6] text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <div>
                      <p className={`text-sm font-semibold ${active ? 'text-[#1d4ed8]' : 'text-gray-700'}`}>{label}</p>
                      <p className="text-xs text-gray-500">
                        {id === 'text' ? 'Quick written update' : id === 'image' ? 'Photo and caption post' : id === 'album' ? 'Several photos and videos' : 'Video link and metadata'}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid max-h-[calc(92vh-196px)] grid-cols-1 overflow-y-auto lg:grid-cols-12">
          <div className="space-y-4 px-6 pt-4 pb-6 lg:col-span-8">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={postType === 'video' ? 'SASE architecture overview' : postType === 'image' ? 'Caption this image' : postType === 'album' ? 'Name this album' : 'Share a learning insight'}
                className="w-full rounded-lg border border-gray-200 bg-gray-100/70 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#60a5fa] focus:bg-white"
                required
              />
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add context, key takeaways, or a call to action..."
                rows={postType === 'text' ? 6 : 4}
                className="w-full resize-none rounded-lg border border-gray-200 bg-gray-100/70 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#60a5fa] focus:bg-white"
              />
              <p className="mt-1 text-right text-[11px] text-gray-400">{description.length} characters</p>
            </div>

            {postType === 'album' && (
              <MediaComposer items={albumItems} onChange={setAlbumItems} disabled={localUploadPct !== null} />
            )}

            {postType === 'image' && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Image</label>
                {imagePreview ? (
                  <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
                    <img src={imagePreview} alt="Preview" className="h-64 w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => { setImageFile(null); setImagePreview(null); }}
                      className="absolute right-3 top-3 rounded-lg bg-gray-900/70 p-1.5 text-white transition hover:bg-gray-900"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex h-40 w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-100 transition hover:border-[#60a5fa] hover:bg-[#eff6ff]/30">
                    <Upload className="mb-3 h-6 w-6 text-gray-500" />
                    <p className="text-sm font-semibold text-gray-700">Drop an image here or browse</p>
                    <p className="mt-1 text-xs text-gray-500">PNG, JPG, GIF, WebP up to 10 MB</p>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageSelect(f); }}
                    />
                  </label>
                )}
              </div>
            )}

            {postType === 'video' && (
              <div className="rounded-lg border border-gray-200 bg-gray-100/60 p-3.5">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">Video Source</label>
                <div className="mb-3 grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => { setVideoSource('youtube'); setVideoUrl(''); setVideoFile(null); }}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${videoSource === 'youtube' ? 'border-[#93c5fd] bg-[#dbeafe] text-[#1d4ed8]' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                  >
                    YouTube
                  </button>
                  <button
                    type="button"
                    onClick={() => { setVideoSource('vidcast'); setVideoUrl(''); setVideoFile(null); }}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${videoSource === 'vidcast' ? 'border-[#93c5fd] bg-[#dbeafe] text-[#1d4ed8]' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                  >
                    Vidcast
                  </button>
                  <button
                    type="button"
                    onClick={() => { setVideoSource('upload'); setVideoUrl(''); }}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${videoSource === 'upload' ? 'border-[#93c5fd] bg-[#dbeafe] text-[#1d4ed8]' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                  >
                    Upload
                  </button>
                </div>

                {videoSource === 'upload' ? (
                  videoFile ? (
                    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-2.5">
                      <Film className="h-8 w-8 shrink-0 text-[#3b82f6]" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-700 truncate">{videoFile.name}</p>
                        <p className="text-[11px] text-gray-400">{(videoFile.size / 1024 / 1024).toFixed(1)} MB</p>
                      </div>
                      <button type="button" onClick={() => setVideoFile(null)} className="shrink-0 p-1 rounded hover:bg-gray-100 text-gray-400">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex h-28 w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-100 transition hover:border-[#60a5fa] hover:bg-[#eff6ff]/30">
                      <Upload className="mb-2 h-5 w-5 text-gray-500" />
                      <p className="text-sm font-semibold text-gray-700">Select a video file</p>
                      <p className="mt-0.5 text-xs text-gray-500">MP4, MOV, WebM</p>
                      <input
                        type="file"
                        accept="video/mp4,video/quicktime,video/webm,video/*"
                        className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) setVideoFile(f); }}
                      />
                    </label>
                  )
                ) : (
                  <>
                    <input
                      type="url"
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e.target.value)}
                      placeholder={videoSource === 'youtube' ? 'https://www.youtube.com/watch?v=...' : 'https://app.vidcast.io/share/embed/...'}
                      className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#60a5fa]"
                    />
                    {videoSource === 'youtube' && extractYouTubeId(videoUrl) && (
                      <div className="mt-3 flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-2.5">
                        <img
                          src={`https://img.youtube.com/vi/${extractYouTubeId(videoUrl)}/default.jpg`}
                          alt=""
                          className="h-12 w-16 rounded-lg object-cover"
                        />
                        <div>
                          <p className="text-xs font-semibold text-gray-700">YouTube preview detected</p>
                          {detectingDuration && <p className="mt-0.5 text-[11px] text-[#2563eb]">Fetching metadata...</p>}
                        </div>
                      </div>
                    )}
                    {videoSource === 'vidcast' && videoUrl.includes('app.vidcast.io/share/embed/') && (
                      <div className="mt-3 rounded-lg border border-gray-200 bg-white overflow-hidden">
                        <div className="aspect-video">
                          <iframe
                            referrerPolicy="strict-origin-when-cross-origin"
                            src={videoUrl}
                            className="w-full h-full"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title="Vidcast preview"
                          />
                        </div>
                        <div className="px-3 py-2 border-t border-gray-100">
                          <p className="text-xs font-semibold text-gray-700">Vidcast video detected</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <aside className="border-t border-gray-200 bg-gray-100/70 px-6 pt-4 pb-6 lg:col-span-4 lg:border-l lg:border-t-0">
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-gray-800 focus:outline-none focus:border-[#60a5fa]"
                >
                  {CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Tags</label>
                <input
                  type="text"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="SASE, ZeroTrust"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#60a5fa]"
                />
                <p className="mt-1.5 text-[11px] text-gray-500">Separate tags with commas.</p>
                {parsedTags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {parsedTags.map(tag => (
                      <span key={tag} className="rounded-md border border-[#bfdbfe] bg-[#eff6ff] px-2 py-0.5 text-[11px] font-medium text-[#1d4ed8]">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Readiness</p>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-gray-600">Required fields</span>
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${canSubmit() ? 'bg-[#d1fae5] text-[#047857]' : 'bg-[#fef3c7] text-[#b45309]'}`}>
                    {canSubmit() ? <Check className="h-3 w-3" /> : null}
                    {canSubmit() ? 'Ready to post' : 'Needs input'}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2.5">
              <button
                type="button"
                onClick={reset}
                disabled={localUploadPct !== null}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit()}
                className="rounded-xl bg-[#3b82f6] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {localUploadPct !== null
                  ? `Uploading… ${localUploadPct}%`
                  : 'Publish'}
              </button>
            </div>
            {localUploadPct !== null && (
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                  className="h-full rounded-full bg-[#3b82f6] transition-all duration-200"
                  style={{ width: `${localUploadPct}%` }}
                />
              </div>
            )}
          </aside>
        </form>
      </div>
    </div>
  );
}


interface EditFields {
  title: string
  description: string
  category: string
  tags: string[]
}

function EditPostModal({ post, onClose, onSave }: {
  post: Post
  onClose: () => void
  onSave: (post: Post, fields: EditFields, items: ComposerItem[] | null) => Promise<void>
}) {
  const [title, setTitle] = useState(post.title);
  const [description, setDescription] = useState(post.description);
  const [category, setCategory] = useState(post.category);
  const [tagsInput, setTagsInput] = useState(post.tags.join(', '));
  const [items, setItems] = useState<ComposerItem[]>(() => toComposerItems(post.media));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasMedia = post.media.length > 0;
  const mediaEditable = hasMedia && canEditMedia(post.media);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || saving) return;
    const tags = tagsInput.split(',').map(t => t.trim().replace(/^#/, '')).filter(Boolean);
    setSaving(true);
    setError(null);
    try {
      await onSave(post, { title: title.trim(), description: description.trim(), category, tags }, mediaEditable ? items : null);
    } catch {
      setError('Could not save your changes. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={saving ? undefined : onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 p-6 border border-gray-100 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">Edit Post</h2>
          <button onClick={onClose} disabled={saving} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#60a5fa] focus:bg-white" required />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full resize-none px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#60a5fa] focus:bg-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#60a5fa]">
                {CATEGORIES.filter(c => c.id !== 'all').map(cat => (<option key={cat.id} value={cat.id}>{cat.label}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">Tags</label>
              <input type="text" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} className="w-full px-3 py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-[#60a5fa]" />
            </div>
          </div>
          {mediaEditable && (
            <>
              <MediaComposer items={items} onChange={setItems} disabled={saving} />
              {items.length === 0 && (
                <p className="text-xs text-[#b45309]">Saving with no media turns this into a text post.</p>
              )}
            </>
          )}
          {hasMedia && !mediaEditable && (
            <p className="text-xs text-gray-500">Media on this post cannot be edited here.</p>
          )}
          <div className="bg-gray-100 rounded-lg p-3 text-xs text-gray-500">
            <span className="font-medium text-gray-600">{post.videoSource === 'youtube' ? 'YouTube' : 'Vidcast'}</span> • {post.duration} • {post.views} views
          </div>
          {error && <p role="alert" className="text-xs font-medium text-[#dc2626]">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} disabled={saving} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-100 transition disabled:opacity-40">Cancel</button>
            <button type="submit" disabled={!title.trim() || saving} className="flex-1 py-2.5 bg-[#3b82f6] hover:bg-[#2563eb] text-white font-semibold rounded-xl disabled:opacity-40 transition">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}


export default function SocialPage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentsMap, setCommentsMap] = useState<Record<string, Comment[]>>({});
  const [loadingCommentsFor, setLoadingCommentsFor] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState('New');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [showNewPost, setShowNewPost] = useState(false);
  const [, setUploadProgress] = useState<number | null>(null);
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const [playingVideo, setPlayingVideo] = useState<Post | null>(null);
  const [showMyPosts, setShowMyPosts] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [copiedPostId, setCopiedPostId] = useState<string | null>(null);
  const currentEmail = useSelector((state: RootState) => state.user.userData?.email) || null;
  const [trendingTags, setTrendingTags] = useState<{ tag: string; count: number }[]>([]);

  const [nextPage, setNextPage] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const feedParams = useCallback((page: number) => {
    const params = new URLSearchParams();
    params.set('sort', SORT_PARAM_MAP[sortBy] || 'new');
    if (activeCategory !== 'all') params.set('category', activeCategory);
    if (activeTag) params.set('tag', activeTag);
    if (searchQuery.trim()) params.set('search', searchQuery.trim());
    params.set('page', String(page));
    return params;
  }, [sortBy, activeCategory, activeTag, searchQuery]);

  const fetchFeedPage = useCallback(async (page: number) => {
    const path = showMyPosts
      ? `${API_BASE}/posts/my/?page=${page}`
      : `${API_BASE}/posts/?${feedParams(page)}`;
    const data = await apiFetch(path);
    const results = Array.isArray(data) ? data : (data?.results ?? []);
    const hasNext = !Array.isArray(data) && !!data?.next;
    return { posts: results.map((p: any) => mapPost(p, currentEmail)), hasNext };
  }, [showMyPosts, feedParams, currentEmail]);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const { posts: first, hasNext } = await fetchFeedPage(1);
      setPosts(first);
      setNextPage(hasNext ? 2 : null);
    } catch {
      setPosts([]);
      setNextPage(null);
    } finally {
      setLoading(false);
    }
  }, [fetchFeedPage]);

  const loadMorePosts = useCallback(async () => {
    if (nextPage === null || loadingMore) return;
    setLoadingMore(true);
    try {
      const { posts: more, hasNext } = await fetchFeedPage(nextPage);
      setPosts(prev => {
        const seen = new Set(prev.map(p => p.id));
        return [...prev, ...more.filter((p: Post) => !seen.has(p.id))];
      });
      setNextPage(hasNext ? nextPage + 1 : null);
    } catch {
      setNextPage(null);
    } finally {
      setLoadingMore(false);
    }
  }, [nextPage, loadingMore, fetchFeedPage]);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || nextPage === null || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) loadMorePosts();
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [nextPage, loadMorePosts, posts.length]);

  const loadTrendingTags = useCallback(async () => {
    try {
      const data = await apiFetch(`${API_BASE}/tags/trending/`);
      const tags = (Array.isArray(data) ? data : (data?.results ?? [])).map((t: any) => ({
        tag: t.name || t.tag,
        count: t.count,
      }));
      setTrendingTags(tags);
    } catch {
      // ignore trending tags fetch failures
    }
  }, []);

  useEffect(() => { loadPosts(); }, [loadPosts]);
  useEffect(() => { loadTrendingTags(); }, [loadTrendingTags]);

  useEffect(() => {
    if (!currentEmail) return;
    setPosts(prev => prev.map(p => ({ ...p, isOwner: p.authorEmail === currentEmail })));
  }, [currentEmail]);

  const loadComments = useCallback(async (postId: string) => {
    if (!postId) return;
    if (commentsMap[postId] !== undefined) return;
    setLoadingCommentsFor(prev => new Set(prev).add(postId));
    try {
      const data = await apiFetch(`${API_BASE}/posts/${postId}/comments/`);
      const results = Array.isArray(data) ? data : (data?.results ?? []);
      setCommentsMap(prev => ({ ...prev, [postId]: results.map((c: any) => mapComment(c, currentEmail)) }));
    } catch {
      setCommentsMap(prev => ({ ...prev, [postId]: [] }));
    } finally {
      setLoadingCommentsFor(prev => { const s = new Set(prev); s.delete(postId); return s; });
    }
  }, [commentsMap, currentEmail]);

  const handleVote = useCallback(async (postId: string, dir: 'up' | 'down') => {
    setPosts(prev => prev.map(p => {
      if (p.id !== postId) return p;
      const wasSame = p.myVote === dir;
      let up = p.upvotes, down = p.downvotes;
      if (p.myVote === 'up') up--;
      if (p.myVote === 'down') down--;
      if (!wasSame) { if (dir === 'up') up++; else down++; }
      return { ...p, upvotes: up, downvotes: down, myVote: wasSame ? null : dir };
    }));
    try {
      await apiFetch(`${API_BASE}/posts/${postId}/vote/`, { method: 'POST', body: JSON.stringify({ direction: dir }) });
    } catch {
      loadPosts();
    }
  }, [loadPosts]);

  const handleLike = useCallback(async (postId: string) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, liked: !p.liked } : p));
    try {
      await apiFetch(`${API_BASE}/posts/${postId}/like/`, { method: 'POST' });
    } catch {
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, liked: !p.liked } : p));
    }
  }, []);

  const handleBookmark = useCallback(async (postId: string) => {
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, bookmarked: !p.bookmarked } : p));
    try {
      await apiFetch(`${API_BASE}/posts/${postId}/bookmark/`, { method: 'POST' });
    } catch {
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, bookmarked: !p.bookmarked } : p));
    }
  }, []);

  const handlePlayVideo = useCallback((post: Post) => {
    apiFetch(`${API_BASE}/posts/${post.id}/`).catch(() => {});
    setPosts(prev => prev.map(p => p.id === post.id ? { ...p, views: p.views + 1 } : p));
    setPlayingVideo(post);
  }, []);

  const handleNewPost = useCallback(async (data: NewPostData) => {
    try {
      let created: any;
      if (data.postType === 'image') {
        try {
          const objectKey = await uploadImageViaPresign(data.imageFile);
          created = await apiFetch(`${API_BASE}/posts/`, {
            method: 'POST',
            body: JSON.stringify({
              post_type: 'image',
              title: data.title,
              description: data.description,
              category: data.category,
              tags: data.tags,
              image_key: objectKey,
            }),
          });
        } catch {
          const form = new FormData();
          form.append('post_type', 'image');
          form.append('title', data.title);
          form.append('description', data.description);
          form.append('category', data.category);
          form.append('tags', JSON.stringify(data.tags));
          form.append('image', data.imageFile);
          const headers = getApiHeaders(null, 'POST', `${API_BASE}/posts/`);
          delete headers['Content-Type'];
          const res = await fetch(buildApiUrl(`${API_BASE}/posts/`), { method: 'POST', headers, body: form });
          if (!res.ok) throw new Error(`${res.status}`);
          created = await res.json();
        }
      } else if (data.postType === 'album') {
        try {
          const media = await buildMediaPayload(data.items, {
            image: uploadImageViaPresign,
            video: (file) => uploadVideoViaPresign(file, (pct) => setUploadProgress(pct)),
            duration: readVideoDuration,
          });
          created = await apiFetch(`${API_BASE}/posts/`, {
            method: 'POST',
            body: JSON.stringify({
              title: data.title,
              description: data.description,
              category: data.category,
              tags: data.tags,
              media,
            }),
          });
        } finally {
          setUploadProgress(null);
        }
      } else if (data.postType === 'video' && 'videoFile' in data) {
        try {
          const objectKey = await uploadVideoViaPresign(
            data.videoFile,
            (pct) => setUploadProgress(pct),
          );
          setUploadProgress(null);
          created = await apiFetch(`${API_BASE}/posts/`, {
            method: 'POST',
            body: JSON.stringify({
              post_type: 'video',
              video_source: 'upload',
              title: data.title,
              description: data.description,
              category: data.category,
              tags: data.tags,
              video_key: objectKey,
            }),
          });
        } catch {
          setUploadProgress(null);
          const form = new FormData();
          form.append('post_type', 'video');
          form.append('video_source', 'upload');
          form.append('title', data.title);
          form.append('description', data.description);
          form.append('category', data.category);
          form.append('tags', JSON.stringify(data.tags));
          form.append('video_file', data.videoFile);
          const headers = getApiHeaders(null, 'POST', `${API_BASE}/posts/`);
          delete headers['Content-Type'];
          const res = await fetch(buildApiUrl(`${API_BASE}/posts/`), { method: 'POST', headers, body: form });
          if (!res.ok) throw new Error(`${res.status}`);
          created = await res.json();
        }
      } else {
        created = await apiFetch(`${API_BASE}/posts/`, {
          method: 'POST',
          body: JSON.stringify({
            post_type: data.postType,
            title: data.title,
            description: data.description,
            category: data.category,
            tags: data.tags,
            ...(data.postType === 'video' ? { video_url: data.videoUrl, video_source: data.videoSource, duration: data.duration || '0:00' } : {}),
          }),
        });
      }
      if ((data.postType === 'image' || data.postType === 'video') && created.id) {
        created = await enrichPostWithMediaUrls(created, created.id);
      }
      const newPost = mapPost(created, currentEmail);
      setPosts(prev => [newPost, ...prev]);
      loadTrendingTags();
    } catch { /* form already closed */ }
  }, [currentEmail, loadTrendingTags]);

  const handleAddComment = useCallback(async (postId: string, text: string) => {
    if (!postId) return;
    try {
      const created = await apiFetch(`${API_BASE}/posts/${postId}/comments/`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      });
      const newComment = mapComment(created, currentEmail);
      setCommentsMap(prev => ({ ...prev, [postId]: [...(prev[postId] || []), newComment] }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, commentsCount: p.commentsCount + 1 } : p));
    } catch {
      // ignore add comment failures
    }
  }, [currentEmail]);

  const handleLikeComment = useCallback(async (postId: string, commentId: string) => {
    setCommentsMap(prev => ({
      ...prev,
      [postId]: (prev[postId] || []).map(c =>
        c.id !== commentId ? c : { ...c, likedByMe: !c.likedByMe, likes: c.likedByMe ? c.likes - 1 : c.likes + 1 }
      ),
    }));
    try {
      await apiFetch(`${API_BASE}/comments/${commentId}/like/`, { method: 'POST' });
    } catch {
      setCommentsMap(prev => ({
        ...prev,
        [postId]: (prev[postId] || []).map(c =>
          c.id !== commentId ? c : { ...c, likedByMe: !c.likedByMe, likes: c.likedByMe ? c.likes - 1 : c.likes + 1 }
        ),
      }));
    }
  }, []);

  const handleDeleteComment = useCallback(async (postId: string, commentId: string) => {
    setCommentsMap(prev => ({ ...prev, [postId]: (prev[postId] || []).filter(c => c.id !== commentId) }));
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, commentsCount: Math.max(0, p.commentsCount - 1) } : p));
    try {
      await apiFetch(`${API_BASE}/comments/${commentId}/`, { method: 'DELETE' });
    } catch { loadPosts(); }
  }, [loadPosts]);

  const handleDeletePost = useCallback(async (postId: string) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
    try {
      await apiFetch(`${API_BASE}/posts/${postId}/`, { method: 'DELETE' });
      loadTrendingTags();
    } catch { loadPosts(); }
  }, [loadPosts, loadTrendingTags]);

  const handleEditPost = useCallback(async (post: Post, fields: EditFields, items: ComposerItem[] | null) => {
    const body: Record<string, unknown> = {
      title: fields.title,
      description: fields.description,
      category: fields.category,
      tags: fields.tags,
    };
    if (items) {
      const media = await buildMediaPatch(post.media, items, {
        image: uploadImageViaPresign,
        video: (file) => uploadVideoViaPresign(file),
        duration: readVideoDuration,
      });
      if (media) body.media = media;
    }
    let updated = await apiFetch(`${API_BASE}/posts/${post.id}/`, { method: 'PATCH', body: JSON.stringify(body) });
    if (updated?.id && (updated.media?.length ?? 0) < 2) updated = await enrichPostWithMediaUrls(updated, updated.id);
    const mapped = mapPost(updated, currentEmail);
    setPosts(prev => prev.map(p => {
      if (p.id !== post.id) return p;
      return updated?.author ? { ...mapped, isOwner: p.isOwner } : { ...p, title: mapped.title, description: mapped.description, category: mapped.category, tags: mapped.tags, media: mapped.media };
    }));
    setEditingPost(null);
  }, [currentEmail]);

  const toggleComments = useCallback((postId: string) => {
    if (!postId) return;
    setExpandedComments(prev => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
        loadComments(postId);
      }
      return next;
    });
  }, [loadComments]);

  const handleTagClick = useCallback((tag: string) => {
    setActiveTag(prev => prev === tag ? null : tag);
    setShowMyPosts(false);
    setActiveCategory('all');
  }, []);

  const handleSharePost = useCallback((postId: string) => {
    const url = `${window.location.origin}/p2p?post=${postId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedPostId(postId);
      setTimeout(() => setCopiedPostId(null), 2000);
    });
  }, []);

  const myPosts = posts.filter(p => p.isOwner);
  const filteredPosts = posts;

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6">
        <aside className="w-56 shrink-0 hidden lg:block">
          <div className="sticky top-20 space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
              <h3 className="text-[11px] font-bold uppercase text-gray-400 tracking-wider mb-3 px-1">Categories</h3>
              <div className="space-y-0.5">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => { setActiveCategory(cat.id); setShowMyPosts(false); setActiveTag(null); }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition text-left ${
                      activeCategory === cat.id && !showMyPosts && !activeTag
                        ? 'bg-[#eff6ff] text-[#2563eb]'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
              <h3 className="text-[11px] font-bold uppercase text-gray-400 tracking-wider mb-3 px-1">Popular Tags</h3>
              <div className="flex flex-wrap gap-1.5">
                {trendingTags.map(item => {
                  const isActive = activeTag === item.tag;
                  return (
                    <button
                      key={item.tag}
                      onClick={() => handleTagClick(item.tag)}
                      className={`px-2.5 py-1 text-xs rounded-lg font-medium transition ${
                        isActive ? 'bg-[#3b82f6] text-white' : 'bg-gray-100 text-gray-600 hover:bg-[#eff6ff] hover:text-[#2563eb]'
                      }`}
                    >
                      #{item.tag}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-5">
            <div className="relative flex-1 max-w-[240px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full bg-white border border-gray-200 rounded-xl pl-9 pr-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-[#60a5fa] focus:ring-1 focus:ring-[#60a5fa]/20 placeholder-gray-400 shadow-sm"
              />
            </div>
            <div className="flex items-center bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
              {SORT_OPTIONS.map(opt => (
                <button
                  key={opt}
                  onClick={() => { setSortBy(opt); setShowMyPosts(false); }}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition ${
                    sortBy === opt && !showMyPosts
                      ? 'bg-[#3b82f6] text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {opt}
                </button>
              ))}
              <div className="w-px h-5 bg-gray-200 mx-1" />
              <button
                onClick={() => { setShowMyPosts(!showMyPosts); setActiveCategory('all'); setActiveTag(null); }}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition ${
                  showMyPosts ? 'bg-[#f59e0b] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                My Posts
              </button>
            </div>
            <button
              onClick={() => setShowNewPost(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#3b82f6] hover:bg-[#2563eb] text-white rounded-xl text-sm font-medium transition shrink-0 shadow-sm"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New Post</span>
            </button>
          </div>

          {activeTag && (
            <div className="flex items-center gap-2 mb-4 px-4 py-2.5 bg-[#eff6ff] border border-[#dbeafe] rounded-xl">
              <Hash className="w-4 h-4 text-[#3b82f6]" />
              <span className="text-sm font-medium text-[#1d4ed8]">#{activeTag}</span>
              <span className="text-xs text-[#3b82f6]">({filteredPosts.length} posts)</span>
              <button onClick={() => setActiveTag(null)} className="ml-auto text-xs text-[#3b82f6] hover:text-[#1d4ed8] font-medium">Clear filter</button>
            </div>
          )}

          {showMyPosts && (
            <div className="flex items-center gap-2 mb-4 px-4 py-2.5 bg-[#fffbeb] border border-[#fef3c7] rounded-xl">
              <Edit3 className="w-4 h-4 text-[#d97706]" />
              <span className="text-sm font-bold text-[#92400e]">Managing Your Posts</span>
              <span className="text-xs text-[#d97706] bg-[#fef3c7] px-2 py-0.5 rounded-full font-medium">{myPosts.length}</span>
              <button onClick={() => setShowMyPosts(false)} className="ml-auto text-xs text-[#d97706] hover:text-[#92400e] font-medium">✕ Exit</button>
            </div>
          )}

          <div className="space-y-4">
            {loading && (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
                <Loader2 className="w-8 h-8 text-gray-300 animate-spin mx-auto mb-3" />
                <p className="text-gray-400 text-sm">Loading posts...</p>
              </div>
            )}

            {!loading && filteredPosts.length === 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
                <Shield className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-400 text-sm font-medium">
                  {showMyPosts ? "You haven't posted yet" : activeTag ? `No posts tagged #${activeTag}` : 'No posts found'}
                </p>
              </div>
            )}

            {!loading && filteredPosts.map(post => {
              const ytId = post.videoSource === 'youtube' ? extractYouTubeId(post.videoUrl) : null;
              const catLabel = getCategoryLabel(post.category);
              const catColor = getCategoryColor(post.category);
              const isExpanded = expandedComments.has(post.id);
              const score = post.upvotes - post.downvotes;

              if (showMyPosts) {
                return (
                  <div key={post.id} className="bg-white rounded-2xl border-2 border-[#fde68a] p-4 shadow-sm">
                    <div className="flex gap-4 items-center">
                      <div className="w-28 h-16 rounded-xl overflow-hidden bg-gray-100 shrink-0">
                        {post.videoSource === 'youtube' && ytId ? (
                          <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#10b981] to-[#0d9488] flex items-center justify-center">
                            <Play className="w-4 h-4 text-white/80" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-gray-900 truncate">{post.title}</h3>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-white" style={{ backgroundColor: catColor }}>{catLabel}</span>
                          <span>{post.duration}</span>
                          <span>{timeAgo(post.timestamp)}</span>
                          <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{post.views}</span>
                          <span className="flex items-center gap-0.5"><ChevronUp className="w-3 h-3" />{post.upvotes}</span>
                          <span className="flex items-center gap-0.5"><MessageSquare className="w-3 h-3" />{post.commentsCount}</span>
                        </div>
                        {post.tags.length > 0 && (
                          <div className="flex gap-1 mt-1.5">
                            {post.tags.map(t => <span key={t} className="text-[10px] text-[#3b82f6] bg-[#eff6ff] px-1.5 py-0.5 rounded font-medium">#{t}</span>)}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {post.isOwner && (
                          <button onClick={() => setEditingPost(post)} className="px-3 py-1.5 text-xs font-medium text-[#2563eb] bg-[#eff6ff] rounded-lg hover:bg-[#dbeafe] transition flex items-center gap-1">
                            <Edit3 className="w-3 h-3" /> Edit
                          </button>
                        )}
                        {post.isOwner && (
                          <button onClick={() => handleDeletePost(post.id)} className="px-3 py-1.5 text-xs font-medium text-[#dc2626] bg-[#fef2f2] rounded-lg hover:bg-[#fee2e2] transition flex items-center gap-1">
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={post.id} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center gap-0.5 shrink-0">
                      <button onClick={() => handleVote(post.id, 'up')} className={`p-1 rounded-lg transition ${post.myVote === 'up' ? 'text-[#3b82f6] bg-[#eff6ff]' : 'text-gray-400 hover:text-[#3b82f6] hover:bg-[#eff6ff]'}`}>
                        <ChevronUp className="w-5 h-5" />
                      </button>
                      <span className={`text-sm font-bold tabular-nums ${post.myVote === 'up' ? 'text-[#3b82f6]' : post.myVote === 'down' ? 'text-[#ef4444]' : 'text-gray-700'}`}>{score}</span>
                      <button onClick={() => handleVote(post.id, 'down')} className={`p-1 rounded-lg transition ${post.myVote === 'down' ? 'text-[#ef4444] bg-[#fef2f2]' : 'text-gray-400 hover:text-[#ef4444] hover:bg-[#fef2f2]'}`}>
                        <ChevronDown className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <div className={`w-6 h-6 rounded-full ${getAvatarColor(post.avatar)} flex items-center justify-center text-white text-[9px] font-bold`}>{post.avatar}</div>
                        <span className="text-sm font-medium text-gray-700">{post.author}</span>
                        <span className="text-xs text-gray-400">• {timeAgo(post.timestamp)}</span>
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold text-white" style={{ backgroundColor: catColor }}>{catLabel}</span>
                        {post.isOwner && (
                          <div className="ml-auto flex items-center gap-1">
                            <button onClick={() => setEditingPost(post)} className="p-1 rounded-lg text-gray-400 hover:text-[#3b82f6] hover:bg-[#eff6ff] transition"><Edit3 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => handleDeletePost(post.id)} className="p-1 rounded-lg text-gray-400 hover:text-[#ef4444] hover:bg-[#fef2f2] transition"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        )}
                      </div>

                      <h3 className="text-[15px] font-bold text-gray-900 mb-1 leading-snug">{post.title}</h3>
                      {post.description && <p className="text-sm text-gray-500 mb-2 leading-relaxed">{post.description}</p>}

                      {post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {post.tags.map(t => (
                            <button
                              key={t}
                              onClick={() => handleTagClick(t)}
                              className={`text-xs px-2 py-0.5 rounded-md font-medium transition ${
                                activeTag === t ? 'bg-[#3b82f6] text-white' : 'bg-gray-100 text-gray-500 hover:bg-[#eff6ff] hover:text-[#2563eb]'
                              }`}
                            >
                              #{t}
                            </button>
                          ))}
                        </div>
                      )}

                      {post.media.length > 1 && (
                        <MediaGrid postId={post.id} items={post.media} title={post.title} />
                      )}

                      {post.media.length < 2 && post.postType === 'image' && post.hasImage && (
                        <div className="rounded-xl overflow-hidden mb-3 border border-gray-100">
                          <PrivateMedia postId={post.id} type="image" alt={post.title} className="w-full max-h-80 object-cover" preloadedUrl={post.imageUrl} />
                        </div>
                      )}

                      {post.media.length < 2 && (post.postType === 'video' || !post.postType) && (
                      <div className="relative rounded-xl overflow-hidden mb-3">
                        {post.hasVideoFile ? (
                          <PrivateMedia postId={post.id} type="video" className="w-full rounded-xl" preloadedUrl={post.videoFileUrl} />
                        ) : (
                        <div className="cursor-pointer group" onClick={() => handlePlayVideo(post)}>
                        {post.videoSource === 'youtube' && ytId ? (
                          <div className="aspect-video relative bg-gray-100">
                            <img src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-14 h-14 rounded-full bg-[#dc2626]/90 backdrop-blur shadow-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                                <Play className="w-6 h-6 text-white ml-0.5" fill="white" />
                              </div>
                            </div>
                            <div className="absolute top-3 left-3 px-2 py-0.5 rounded-md bg-[#dc2626] text-[10px] text-white font-bold flex items-center gap-1"><Play className="w-2.5 h-2.5" fill="white" />YouTube</div>
                            <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded-md bg-black/60 backdrop-blur text-[11px] text-white font-medium">{post.duration}</div>
                          </div>
                        ) : (
                          <div className="aspect-video relative overflow-hidden bg-black">
                            <div style={{ position: 'absolute', inset: 0, clipPath: 'inset(0 0 55px 0)' }}>
                              <iframe
                                referrerPolicy="strict-origin-when-cross-origin"
                                src={post.videoUrl}
                                className="w-full h-full"
                                allow="autoplay; fullscreen; picture-in-picture"
                                title={post.title}
                                style={{ pointerEvents: 'none', display: 'block' }}
                              />
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                              <div className="w-14 h-14 rounded-full bg-white/90 shadow-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                                <Play className="w-6 h-6 text-gray-800 ml-0.5" fill="currentColor" />
                              </div>
                            </div>
                            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-black/40 backdrop-blur text-[10px] text-white font-bold">Vidcast</div>
                          </div>
                        )}
                        </div>
                        )}
                      </div>
                      )}

                      <div className="flex items-center gap-1">
                        <button onClick={() => handleLike(post.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${post.liked ? 'text-[#f43f5e] bg-[#fff1f2]' : 'text-gray-500 hover:bg-gray-100'}`}>
                          <Heart className="w-3.5 h-3.5" fill={post.liked ? 'currentColor' : 'none'} /> Like
                        </button>
                        <button onClick={() => toggleComments(post.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${isExpanded ? 'text-[#3b82f6] bg-[#eff6ff]' : 'text-gray-500 hover:bg-gray-100'}`}>
                          <MessageSquare className="w-3.5 h-3.5" /> {post.commentsCount}
                        </button>
                        <button onClick={() => handleSharePost(post.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${copiedPostId === post.id ? 'text-[#16a34a] bg-[#f0fdf4]' : 'text-gray-500 hover:bg-gray-100'}`}>
                          {copiedPostId === post.id ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Share2 className="w-3.5 h-3.5" /> Share</>}
                        </button>
                        <button onClick={() => handleBookmark(post.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${post.bookmarked ? 'text-[#f59e0b] bg-[#fffbeb]' : 'text-gray-500 hover:bg-gray-100'}`}>
                          <Bookmark className="w-3.5 h-3.5" fill={post.bookmarked ? 'currentColor' : 'none'} /> Save
                        </button>
                        <span className="flex items-center gap-1 text-[11px] text-gray-300 ml-auto"><Eye className="w-3.5 h-3.5" />{(post.views ?? 0).toLocaleString()}</span>
                      </div>

                      {isExpanded && (
                        <CommentSection
                          postId={post.id}
                          comments={commentsMap[post.id] || []}
                          loadingComments={loadingCommentsFor.has(post.id)}
                          onAddComment={handleAddComment}
                          onLikeComment={handleLikeComment}
                          onDeleteComment={handleDeleteComment}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {!loading && nextPage !== null && (
              <div ref={loadMoreRef} className="py-4 text-center">
                <button
                  onClick={loadMorePosts}
                  disabled={loadingMore}
                  className="text-sm text-[#3b82f6] hover:underline disabled:text-gray-400"
                >
                  {loadingMore ? 'Loading more...' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        </div>

        <aside className="w-64 shrink-0 hidden xl:block">
          <div className="sticky top-20 space-y-5">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#f97316]" />
                Trending Tags
              </h3>
              <div className="space-y-2">
                {trendingTags.map((item, i) => (
                  <button
                    key={item.tag}
                    onClick={() => handleTagClick(item.tag)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-left transition ${
                      activeTag === item.tag ? 'bg-[#eff6ff] border border-[#bfdbfe]' : 'hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-[11px] font-bold text-gray-300 w-4">{i + 1}</span>
                    <Hash className="w-3.5 h-3.5 text-[#60a5fa]" />
                    <span className={`text-sm font-medium flex-1 ${activeTag === item.tag ? 'text-[#2563eb]' : 'text-gray-700'}`}>{item.tag}</span>
                    <span className="text-[11px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{item.count}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
              <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#3b82f6]" />
                Most Viewed
              </h3>
              <div className="space-y-3">
                {[...posts].sort((a, b) => b.views - a.views).slice(0, 4).map((p, i) => (
                  <div key={p.id} className="flex items-start gap-2.5 group cursor-pointer" onClick={() => handlePlayVideo(p)}>
                    <span className="text-[11px] font-bold text-gray-300 w-4 pt-0.5">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-700 font-medium group-hover:text-[#2563eb] transition leading-relaxed line-clamp-2">{p.title}</p>
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-400">
                        <span>{p.author}</span>
                        <span>•</span>
                        <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{(p.views ?? 0).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>

      {playingVideo && <VideoPlayer post={playingVideo} onClose={() => setPlayingVideo(null)} />}

      <NewPostModal isOpen={showNewPost} onClose={() => setShowNewPost(false)} onSubmit={handleNewPost} onUploadProgress={setUploadProgress} />

      {editingPost && <EditPostModal post={editingPost} onClose={() => setEditingPost(null)} onSave={handleEditPost} />}
    </div>
  );
}
